import { createFileRoute, Link } from "@tanstack/react-router";
import ArrowRightIcon from "lucide-react/dist/esm/icons/arrow-right";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import { useMemo } from "react";
import { z } from "zod";

import { RfmHeatmap } from "@/components/chart/rfm-heatmap";
import { FilterSearch, FilterSelect } from "@/components/data-table/filter-controls";
import { TrendDelta } from "@/components/metric/stat-tile";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { EmptyState } from "@/components/states/state-views";
import { StatusDot } from "@/components/status/status-badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { scenariosFixture } from "@/features/operations/mock-data";
import {
  rfmDistribution,
  SEGMENT_KIND_LABEL,
  type Segment,
  segmentsFixture
} from "@/features/segments/mock-data";
import { formatInteger, formatRelative } from "@/lib/format";
import { useNow } from "@/lib/use-now";

const segmentsSearchSchema = z.object({
  q: z.string().trim().max(100).catch("").default(""),
  kind: z.enum(["all", "rfm", "behavior", "lifecycle", "acquisition"]).catch("all").default("all"),
  selected: z.string().optional()
});

export const Route = createFileRoute("/segments")({
  validateSearch: segmentsSearchSchema,
  component: SegmentsPage
});

function scenarioName(id: string): string {
  return scenariosFixture.find((scenario) => scenario.id === id)?.name ?? id;
}

/**
 * RFM ベースの動的セグメント。誰に配信するかの定義であり、シナリオが参照する源（第5章）。
 * 顧客（個票の明細）とは別物で、こちらは「ルール」を扱う。
 */
function SegmentsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const now = useNow();

  const update = (patch: Partial<typeof search>) => {
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });
  };

  const filtered = useMemo(() => {
    const keyword = search.q.toLowerCase();
    return segmentsFixture.filter((segment) => {
      if (search.kind !== "all" && segment.kind !== search.kind) return false;
      if (!keyword) return true;
      return `${segment.name} ${segment.description}`.toLowerCase().includes(keyword);
    });
  }, [search.q, search.kind]);

  const selected = segmentsFixture.find((segment) => segment.id === search.selected);
  const totalMembers = segmentsFixture.reduce((sum, segment) => sum + segment.memberCount, 0);

  return (
    <>
      <PageHeader
        kind="data"
        title="セグメント"
        description="RFMベースの動的な配信対象。定義に一致する顧客が自動で出入りする"
        action={
          <Button size="sm">
            <PlusIcon aria-hidden="true" />
            セグメントを作成
          </Button>
        }
      />

      <PageBody density="dense" className="grid gap-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <Panel
            title="セグメント一覧"
            count={filtered.length}
            description={`延べ ${formatInteger(totalMembers)}人`}
            flush
          >
            <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
              <FilterSearch
                label="名前・説明"
                value={search.q}
                onChange={(value) => update({ q: value })}
                placeholder="セグメントを検索"
              />
              <FilterSelect
                label="種別"
                value={search.kind}
                onChange={(value) => update({ kind: value })}
                options={[
                  { value: "all", label: "すべて" },
                  { value: "rfm", label: "RFM" },
                  { value: "behavior", label: "行動" },
                  { value: "lifecycle", label: "ライフサイクル" },
                  { value: "acquisition", label: "獲得" }
                ]}
              />
            </div>

            {filtered.length > 0 ? (
              <ul className="divide-y divide-line">
                {filtered.map((segment) => (
                  <li key={segment.id}>
                    <button
                      type="button"
                      onClick={() => update({ selected: segment.id })}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover"
                    >
                      <StatusDot tone={segment.tone} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-fg">
                            {segment.name}
                          </span>
                          <span className="shrink-0 rounded-sm bg-surface-sunken px-1 text-2xs text-fg-muted">
                            {SEGMENT_KIND_LABEL[segment.kind]}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-fg-subtle">
                          {segment.description}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="num block text-sm font-semibold text-fg">
                          {formatInteger(segment.memberCount)}
                        </span>
                        <TrendDelta
                          value={segment.deltaPercent}
                          polarity="neutral"
                          comparisonLabel="30日"
                        />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="条件に一致するセグメントがありません"
                description="検索語を短くするか、種別の絞り込みを解除してください。"
              />
            )}
          </Panel>

          {/* RFM 分布は分布なのでヒートマップで見せる（原則 11-1） */}
          <Panel title="RFM 分布" description="R×F の顧客数">
            <RfmHeatmap cells={rfmDistribution} />
            <p className="mt-3 text-xs text-fg-subtle">
              右上（活発 × 高頻度）が VIP、左下（休眠 × 低頻度）が離脱リスク。
              セグメントはこの分布を切り出したもの。
            </p>
          </Panel>
        </div>
      </PageBody>

      <SegmentDetail
        segment={selected}
        now={now}
        onClose={() =>
          void navigate({ search: (previous) => ({ ...previous, selected: undefined }) })
        }
      />
    </>
  );
}

interface SegmentDetailProps {
  segment: Segment | undefined;
  now: Date;
  onClose: () => void;
}

function SegmentDetail({ segment, now, onClose }: SegmentDetailProps) {
  return (
    <Sheet open={segment !== undefined} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        {segment ? (
          <>
            <SheetHeader className="border-b border-line">
              <SheetTitle className="flex items-center gap-2 text-base">
                <StatusDot tone={segment.tone} />
                {segment.name}
              </SheetTitle>
              <SheetDescription>{segment.description}</SheetDescription>
            </SheetHeader>

            <div className="grid gap-5 overflow-y-auto p-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-line bg-surface p-3">
                  <p className="eyebrow">所属人数</p>
                  <p className="num mt-1 text-2xl font-semibold text-fg">
                    {formatInteger(segment.memberCount)}
                  </p>
                  <TrendDelta
                    value={segment.deltaPercent}
                    polarity="neutral"
                    comparisonLabel="30日"
                  />
                </div>
                <div className="rounded-lg border border-line bg-surface p-3">
                  <p className="eyebrow">最終評価</p>
                  <p className="mt-1 text-sm text-fg">
                    <time dateTime={segment.lastEvaluatedAt}>
                      {formatRelative(segment.lastEvaluatedAt, now)}
                    </time>
                  </p>
                  <p className="mt-1 text-xs text-fg-subtle">定義に一致する顧客を自動更新</p>
                </div>
              </div>

              <section>
                <h3 className="eyebrow mb-2">定義（すべて満たす顧客が所属）</h3>
                <ul className="grid gap-1.5">
                  {segment.rules.map((rule) => (
                    <li
                      key={`${rule.field}-${rule.value}`}
                      className="flex items-center gap-2 rounded-md border border-line bg-surface-sunken px-2.5 py-1.5 text-sm"
                    >
                      <span className="text-fg">{rule.field}</span>
                      <span className="num rounded-sm bg-surface px-1 text-xs font-semibold text-fg-muted">
                        {rule.operator}
                      </span>
                      <span className="font-medium text-fg">{rule.value}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h3 className="eyebrow mb-2">
                  配信対象にしているシナリオ（{segment.usedByScenarioIds.length}）
                </h3>
                {segment.usedByScenarioIds.length > 0 ? (
                  <ul className="grid gap-1">
                    {segment.usedByScenarioIds.map((id) => (
                      <li key={id}>
                        <Link
                          to="/scenarios/$scenarioId"
                          params={{ scenarioId: id }}
                          className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-2 text-sm text-fg transition-colors hover:bg-surface-hover"
                        >
                          {scenarioName(id)}
                          <ArrowRightIcon className="size-3.5 text-fg-subtle" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-fg-subtle">
                    参照しているシナリオはありません（例: 配信抑止リストは除外専用）。
                  </p>
                )}
              </section>
            </div>

            <div className="mt-auto flex items-center gap-2 border-t border-line p-4">
              <Button size="sm" variant="outline" className="flex-1">
                定義を編集
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                顧客を表示
              </Button>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
